import {
  AfterContentChecked,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject
} from '@angular/core';
import {
  validationParams,
  DuplicateValidationService,
  ApiServiceService,
  LoggingService,
  NotificationService
} from '@oneeducation/app-core';
import { USER_ENDPOINT } from '@shared/properties';
import { UserRoleService } from '@usermanagement/services';
import {
  LocalAuthority,
  School,
  userModel
} from '@usermanagement/models/userModel';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ValidateFormDataService } from '@oneeducation/app-core';
import setting from '@config/config.json';
import { Table } from 'primeng/table';
import { AccessibilityService } from '@shared/services';
import { Dialog } from 'primeng/dialog';

@Component({
  selector: 'app-manage-users',
  styleUrls: ['./manage-users.component.scss'],
  templateUrl: './manage-users.component.html',
  standalone: false
})
export class ManageUsersComponent implements OnInit, AfterContentChecked {
  private readonly apiService = inject(ApiServiceService);
  private readonly userService = inject(UserRoleService);
  private readonly notificationService = inject(NotificationService);
  private readonly validateFormDataService = inject(ValidateFormDataService);
  private readonly loggingService = inject(LoggingService);
  private readonly duplicateValidationService = inject(
    DuplicateValidationService
  );
  private readonly accessibilityService = inject(AccessibilityService);
  private readonly elementRef = inject(ElementRef);

  @ViewChildren('firstName', { read: ElementRef })
  firstName: QueryList<ElementRef>;
  @ViewChild('buttonAddNewUser') buttonAddNewUser: ElementRef;
  @ViewChild('allUsers') allUsers: Table;
  @ViewChild('dialogAddNewUser') dialogAddNewUser: Dialog;
  display: boolean;
  user: userModel;
  school: School;
  users: userModel[];
  filteredUsers: userModel[];
  schoolId: number;
  apiRoute: string;
  clonedUsers: { [s: string]: userModel } = {};
  namePattern = '.*\\S+.*';
  emailValidationParams: validationParams;
  userFields = new FormGroup({
    Email: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.email],
      updateOn: 'blur'
    }),
    FirstName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.pattern(this.namePattern)
    ]),
    FamilyName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.pattern(this.namePattern)
    ])
  });
  /* tslint:disable:quotemark */
  emailRegex = new RegExp(
    "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$"
  );
  /* tslint:enable:quotemark */

  headerText: string;
  isSaveClicked: boolean;
  selectedRowIndex = -1;
  isFocusOnRow = false;

  constructor() {
    this.user = new userModel();
    this.school = new School();
    this.user.schools = [];
  }

  ngOnInit(): void {
    this.isSaveClicked = false;
    this.setFocusOnAddNewUserButton();

    if (this.isLAUser()) {
      this.headerText = 'Manage Local Authority Users';
    } else if (this.isSchoolUser()) {
      this.headerText = 'Manage School Users For';
    }
    this.userService.userInfoReceived.subscribe((x) => {
      if (x === true) {
        this.getUsers();
      }
    });
  }

  public setFocusOnAddNewUserButton() {
    setTimeout(() => {
      if (this.buttonAddNewUser) {
        this.buttonAddNewUser.nativeElement.focus();
      }
    }, 0);
  }

  getUsers() {
    this.apiRoute = this.getApiRoute();
    this.fetchUsers();
  }

  get f() {
    return this.userFields.controls;
  }

  fetchUsers() {
    this.apiService.getAll<userModel[]>(this.apiRoute, '').subscribe({
      next: (x: any) => {
        this.users = x.users;

        this.users.sort((a, b) => {
          if (
            a.firstName === null ||
            a.firstName === undefined ||
            a.familyName === null ||
            a.familyName === undefined
          ) {
            return -1;
          } else if (
            b.firstName === null ||
            b.firstName === undefined ||
            b.familyName === null ||
            b.familyName === undefined
          ) {
            return 1;
          }

          const lastName = a.familyName
            .toLowerCase()
            .localeCompare(b.familyName.toLowerCase());
          const firstName = a.firstName
            .toLowerCase()
            .localeCompare(b.firstName.toLowerCase());

          return lastName || firstName;
        });

        if (this.userService.isSchoolAdmin) {
          this.filteredUsers = JSON.parse(JSON.stringify(this.users));
          this.filteredUsers = this.GetFilteredArray();
        } else {
          this.filteredUsers = JSON.parse(JSON.stringify(this.users));
        }
      },
      error: () => {
        this.notificationService.showError('Error getting users');
      }
    });
  }

  schoolChange(event) {
    this.schoolId = event.value;
    if (this.users !== undefined && this.users !== null) {
      this.filteredUsers = JSON.parse(JSON.stringify(this.users));
      this.filteredUsers = this.GetFilteredArray();
    }
  }

  GetFilteredArray() {
    return this.filteredUsers.filter((user) => {
      if (
        user.schools.filter((u) => {
          if (u.schoolId === this.schoolId) {
            return u;
          } else {
            return null;
          }
        }).length > 0
      ) {
        if (user.schools.length > 1) {
          for (let r = user.schools.length - 1; r >= 0; r--) {
            if (user.schools[r].schoolId !== this.schoolId) {
              user.schools.splice(r, 1);
            }
          }
        }
        return user;
      } else {
        return null;
      }
    });
  }

  changeStatus(user: userModel) {
    this.apiService.put(USER_ENDPOINT.UpdateUser, user).subscribe({
      next: () => {
        // Refresh the data for users
        this.fetchUsers();
      },
      error: (x: any) => {
        if (x.error !== null && x.error !== '') {
          this.notificationService.showError(x.error.message);
        }
        if (this.isLAUser()) {
          user.localAuthority.active = !user.localAuthority.active;
        }
        if (this.isSchoolUser()) {
          user.schools[0].active = !user.schools[0].active;
        }
      }
    });
  }

  getApiRoute() {
    if (this.userService.isSchoolAdmin === true) {
      return USER_ENDPOINT.GetUsersByUserType.replace('{usertype}', 'School');
    } else if (this.userService.isLocalAuthorityUser === true) {
      return USER_ENDPOINT.GetUsersByUserType.replace('{usertype}', 'LA');
    }

    return '';
  }

  isLAUser() {
    return this.userService.isLocalAuthorityUser;
  }

  isSchoolUser() {
    return this.userService.isSchoolAdmin;
  }

  onRowEditInit(user, rowIndex) {
    //set focus on firstName of current row when clicked on edit
    this.selectedRowIndex = rowIndex;
    if (this.firstName) {
      setTimeout(() => {
        this.firstName
          .find(
            (x) =>
              x.nativeElement.attributes['ri'].nodeValue === String(rowIndex)
          )
          .nativeElement.focus();
      }, 0);
    }

    this.clonedUsers[user.userId] = { ...user };
    this.SetEmailValidationParam();
  }

  onRowEditSave(user, index: number) {
    if (
      user.firstName.trim() !== '' &&
      user.familyName.trim() !== '' &&
      user.email.trim() !== ''
    ) {
      if (this.emailRegex.test(user.email)) {
        this.apiService.put(USER_ENDPOINT.UpdateUser, user).subscribe({
          next: () => {
            delete this.clonedUsers[user.userId];
            // Refresh user list after saving user
            this.fetchUsers();
          },
          error: (x: any) => {
            if (x.error !== null && x.error !== '') {
              this.notificationService.showError(x.error.message);
              this.onRowEditCancel(user, index);
            }
          }
        });
      }
    }

    this.setFocusOnSelectedTableRow(this.selectedRowIndex);
  }

  onRowEditCancel(user, index: number) {
    this.filteredUsers[index] = this.clonedUsers[user.userId];
    delete this.clonedUsers[user.userId];

    this.setFocusOnSelectedTableRow(this.selectedRowIndex);
  }

  disableSaveButton(user) {
    if (
      user.firstName === null ||
      user.familyName === null ||
      user.firstName.trim() === '' ||
      user.familyName.trim() === '' ||
      user.email.trim() === '' ||
      !this.emailRegex.test(user.email)
    ) {
      return true;
    }
    return false;
  }

  onSendEmail(user) {
    if (user.userId != null && user.userId !== '') {
      this.apiService
        .post(USER_ENDPOINT.SendWelcomeEmail, '', { UserId: user.userId })
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              'Welcome email re-sent successfully to ' + user.email
            );
          },
          error: (x: any) => {
            if (x.error !== null && x.error !== '') {
              this.notificationService.showError(x.error.message);
            }
          }
        });
    }
  }

  saveuser() {
    this.isSaveClicked = true;

    setTimeout(() => {
      if (!this.userFields.valid) {
        this.validateFormDataService.validateAllFormFields(this.userFields);
        this.isSaveClicked = false;
        return;
      }
      // Angular upgrade 14: assign the properties explicitly as reactive forms are strictly typed by default
      this.user.email = this.userFields.value.Email;
      this.user.firstName = this.userFields.value.FirstName;
      this.user.familyName = this.userFields.value.FamilyName;
      this.user.product = { name: setting['product'] };
      let selectedSchoolindex = 0;
      if (this.userService.isSchoolAdmin === true) {
        if (
          this.userService.schoolUserContextId > 0 &&
          this.userService.User !== null &&
          this.userService.User.schools.length > 0
        ) {
          selectedSchoolindex = this.userService.User.schools.findIndex(
            (x) => x.schoolId === this.userService.schoolUserContextId
          );
          this.school.schoolName =
            this.userService.User.schools[selectedSchoolindex].schoolName;
          this.school.schoolNumber =
            this.userService.User.schools[selectedSchoolindex].schoolNumber;
          this.school.laId =
            this.userService.User.schools[selectedSchoolindex].laId;
        }
        this.user.userType = this.userService.userType;
        this.school.active = true;
        if (this.userService.schoolUserContextId > 0) {
          this.school.schoolId = this.userService.schoolUserContextId;
        }
        this.user.schools = [];
        this.user.schools.push(this.school);
      } else if (this.userService.isLocalAuthorityUser === true) {
        this.user.localAuthority = new LocalAuthority();
        this.user.userType = this.userService.userType;
        this.user.localAuthority.active = true;
        this.user.localAuthority.administrator = true;
      }
      this.user.laId = this.userService.LaID;
      this.user.laName = this.userService.LAName;
      this.apiService
        .postWithResponseHeader(USER_ENDPOINT.CreateUser, this.user)
        .subscribe({
          next: (x: any) => {
            if (x.headers.get('location') !== '') {
              this.apiService
                .post(USER_ENDPOINT.SendWelcomeEmail, '', {
                  UserId: x.headers.get('location')
                })
                .subscribe({
                  next: () => {
                    this.notificationService.showSuccess(
                      'Welcome email sent successfully to ' +
                        this.userFields.value.Email
                    );
                  },
                  error: (e: any) => {
                    if (e.error !== null && e.error !== '') {
                      this.notificationService.showError(e.error.message);
                    }
                  }
                });
            }
            this.closePopup();
          },
          error: (x: any) => {
            this.isSaveClicked = false;
            if (x.error.code === '1.1.409.01') {
              this.notificationService.showError(
                'Unable to save user as email address is either missing or invalid.'
              );
              this.loggingService?.logError(x);
            } else if (x.error.code === '1.1.409.02') {
              this.notificationService.showError(
                'Unable to save user, a user already exists with this email address.'
              );
              this.loggingService?.logError(x);
            } else if (x.error.code === '1.1.409.03') {
              this.notificationService.showError(
                'Unable to save user, a user already exists with this email address at another LA.'
              );
              this.loggingService?.logError(x);
            } else {
              this.notificationService.showError(
                'Something went wrong, please try again.'
              );
              this.loggingService?.logError(x);
            }
          }
        });
    }, 1000);
  }

  showCreateUserDialog() {
    this.display = true;
    this.userFields.reset();
    this.SetEmailValidationParam();
    this.userFields.controls.Email.asyncValidator =
      this.duplicateValidationService.validateDuplicates(
        this.emailValidationParams
      );
  }

  private SetEmailValidationParam() {
    const route: string = USER_ENDPOINT.CheckEmail;
    if (this.isSchoolUser()) {
      this.emailValidationParams = {
        apiRoute: route,
        queryParams: {
          schoolid: this.schoolId ? this.schoolId.toString() : '0',
          laid: this.apiService.laId ? this.apiService.laId.toString() : '0'
        }
      };
    } else {
      this.emailValidationParams = {
        apiRoute: route,
        queryParams: {}
      };
    }
  }

  closePopup() {
    this.display = false;
    this.ngOnInit();
  }

  // Set focus on table current table row
  private setFocusOnSelectedTableRow(rowIndex: number) {
    this.accessibilityService.setFocusOnSelectedTableRow(
      this.allUsers,
      rowIndex
    );
  }

  // On press enter key, call the edit method
  public editRowData(rowIndex) {
    if (this.isFocusOnRow) {
      this.accessibilityService.editRowData(this.allUsers, rowIndex);
    }
  }

  ngAfterContentChecked(): void {
    this.setAttributesForAlertBox();
    this.setDialogBoxAriaAttributes();
  }

  private setAttributesForAlertBox() {
    if (this.elementRef) {
      this.accessibilityService.setAttributesForAlertBox(this.elementRef);
    }
  }

  // ARIA dialog and alertdialog nodes should have an accessible name
  private setDialogBoxAriaAttributes() {
    if (this.dialogAddNewUser) {
      this.accessibilityService.setDialogBoxAriaAttributes(
        this.dialogAddNewUser,
        'Add New User'
      );
    }
  }
}
